import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, FolderOpen, Trash2, LayoutTemplate, FileText, ImagePlus, X, Copy } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PLAN_TEMPLATES } from '@/lib/planTemplates';
import { wallSpan } from '@/lib/planMaterials';
import { priceFor, priceUnit } from '@/lib/planPricing';
import { useAuth } from '@/lib/auth';

const unitLabel = (u, q) => (u === 'item' ? (q === 1 ? 'item' : 'items') : u);
const btn = 'inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold hover:border-slate-900 transition-colors';

export function PlanDrafts({ draftId, getState, onLoad, onSaved }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const refresh = () => api.get('/plan-drafts').then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { if (open) refresh(); }, [open]);

  const save = async (quiet = false) => {
    try {
      const body = getState();
      const { data } = draftId ? await api.put(`/plan-drafts/${draftId}`, body) : await api.post('/plan-drafts', body);
      onSaved(data.id); if (!quiet) toast.success(draftId ? 'Plan saved' : 'Plan saved to My plans');
      return data.id;
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || 'Could not save plan'); return null; }
  };
  const [versions, setVersions] = useState([]);
  const loadVersions = () => draftId && api.get(`/plan-drafts/${draftId}/versions`).then(({ data }) => setVersions(data)).catch(() => {});
  useEffect(() => { if (open) loadVersions(); else setVersions([]); }, [open, draftId]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveOption = async () => {
    const existing = draftId ? (await api.get(`/plan-drafts/${draftId}/versions`).catch(() => ({ data: [] }))).data : [];
    const used = new Set(existing.map((v) => v.label));
    const next = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'].map((c) => `Option ${c}`).find((l) => !used.has(l)) || `Option ${existing.length + 1}`;
    const label = window.prompt('Name this option (e.g. “Option A – porcelain patio”)', next);
    if (label === null) return;
    const id = await save(true);
    if (!id) return;
    await api.post(`/plan-drafts/${id}/versions`, { label });
    toast.success(`Saved “${label || 'option'}” — keep designing, earlier options stay safe`);
    if (open) loadVersions();
  };
  const openVersion = async (vid) => {
    const { data } = await api.get(`/plan-drafts/${draftId}/versions/${vid}`);
    onLoad(data); setOpen(false); toast.success(`Showing “${data.label}” — press Save to make it the main plan`);
  };
  const removeVersion = async (vid) => { await api.delete(`/plan-drafts/${draftId}/versions/${vid}`); loadVersions(); };
  const load = async (id) => {
    const { data } = await api.get(`/plan-drafts/${id}`);
    onLoad(data); setOpen(false); toast.success(`Opened “${data.name}”`);
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this saved plan?')) return;
    await api.delete(`/plan-drafts/${id}`); refresh();
    if (id === draftId) onSaved(null);
  };
  return (
    <>
      <button type="button" onClick={() => save()} className={btn} data-testid="plan-save-draft-btn"><Save size={13} /> {draftId ? 'Save' : 'Save draft'}</button>
      <button type="button" onClick={saveOption} className={btn} data-testid="plan-save-option-btn"><Copy size={13} /> Save as option</button>
      <button type="button" onClick={() => setOpen(true)} className={btn} data-testid="plan-open-drafts-btn"><FolderOpen size={13} /> My plans</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="plan-drafts-dialog">
          <DialogHeader><DialogTitle className="font-display">My saved plans</DialogTitle></DialogHeader>
          {!list.length ? <p className="text-sm text-slate-500">No saved plans yet — press “Save draft” to keep this one.</p> : (
            <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
              {list.map((p) => (
                <li key={p.id} className="flex items-center gap-3 py-2.5" data-testid={`plan-draft-${p.id}`}>
                  <button type="button" onClick={() => load(p.id)} className="flex-1 text-left" data-testid={`plan-draft-open-${p.id}`}>
                    <span className="block font-semibold text-slate-900">{p.name}{p.id === draftId && <span className="ml-2 tm-chip">Open</span>}</span>
                    <span className="block text-xs text-slate-500">{[p.client, `${p.shape_count} area${p.shape_count === 1 ? "" : "s"}`, `saved ${new Date(p.updated_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}`].filter(Boolean).join(' · ')}</span>
                  </button>
                  <button type="button" onClick={() => remove(p.id)} className="text-slate-400 hover:text-rose-600" data-testid={`plan-draft-delete-${p.id}`} aria-label="Delete"><Trash2 size={15} /></button>
                </li>
              ))}
            </ul>
          )}
          {draftId && (
            <section className="border-t border-slate-200 pt-3" data-testid="plan-versions">
              <div className="flex items-center justify-between mb-2">
                <p className="tm-label mb-0">Options of the open plan</p>
                <button type="button" onClick={saveOption} className="text-xs font-semibold text-brand hover:underline" data-testid="plan-versions-add">+ Save current as option</button>
              </div>
              {!versions.length ? <p className="text-xs text-slate-500">No options yet — save one to show the customer Option A and Option B.</p> : (
                <ul className="space-y-1.5">
                  {versions.map((v) => (
                    <li key={v.id} className="flex items-center gap-2 text-sm" data-testid={`plan-version-${v.id}`}>
                      <button type="button" onClick={() => openVersion(v.id)} className="flex-1 text-left hover:text-brand" data-testid={`plan-version-open-${v.id}`}>
                        <span className="font-semibold">{v.label}</span>
                        <span className="text-xs text-slate-500"> · {v.shape_count} area{v.shape_count === 1 ? '' : 's'} · {new Date(v.saved_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                      </button>
                      <button type="button" onClick={() => removeVersion(v.id)} className="text-slate-400 hover:text-rose-600" data-testid={`plan-version-delete-${v.id}`} aria-label="Delete option"><Trash2 size={14} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function PlanTemplates({ onApply, hasShapes }) {
  const [open, setOpen] = useState(false);
  const pick = (t) => {
    if (hasShapes && !window.confirm('Replace your current layout with this template?')) return;
    onApply(t); setOpen(false); toast.success(`${t.name} added — drag things to fit your site`);
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={btn} data-testid="plan-templates-btn"><LayoutTemplate size={13} /> Templates</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="plan-templates-dialog">
          <DialogHeader><DialogTitle className="font-display">Start from a template</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            {PLAN_TEMPLATES.map((t) => (
              <button key={t.id} type="button" onClick={() => pick(t)} data-testid={`plan-template-${t.id}`}
                className="tm-card tm-card-interactive p-4 text-left">
                <span className="block font-display text-lg font-semibold">{t.name}</span>
                <span className="block text-xs text-slate-500 mt-1">{t.desc}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function PlanOverlay({ overlay, setOverlay }) {
  const onFile = (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    const url = URL.createObjectURL(f); const img = new Image();
    img.onload = () => setOverlay({ url, img, opacity: 0.6, zoom: 1, dx: 0, dy: 0, split: 0 });
    img.src = url;
  };
  return (
    <span className="inline-flex items-center gap-2">
      <label className={`${btn} cursor-pointer`} data-testid="plan-overlay-btn">
        <ImagePlus size={13} /> {overlay ? 'Change photo' : 'Overlay site photo'}
        <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="plan-overlay-input" />
      </label>
      {overlay && (
        <>
          <label className="text-xs flex items-center gap-1.5">Plan opacity
            <input type="range" min="0.15" max="1" step="0.05" value={overlay.opacity} onChange={(e) => setOverlay({ ...overlay, opacity: +e.target.value })} data-testid="plan-overlay-opacity" className="w-24 accent-brand" />
          </label>
          <button type="button" onClick={() => setOverlay(null)} className="text-slate-400 hover:text-rose-600" data-testid="plan-overlay-remove" aria-label="Remove photo"><X size={15} /></button>
        </>
      )}
    </span>
  );
}

const Slider = ({ label, tid, ...p }) => (
  <label className="text-xs flex items-center gap-2"><span className="w-24 shrink-0 font-semibold text-slate-600">{label}</span>
    <input type="range" {...p} data-testid={tid} className="flex-1 accent-brand" /></label>
);

// Line the garden photo up with the plan, then wipe between the photo (before) and the design (after)
export function PlanOverlayControls({ overlay, setOverlay, onDownload }) {
  const set = (k) => (e) => setOverlay({ ...overlay, [k]: +e.target.value });
  return (
    <div className="tm-card p-4 mt-3 space-y-3" data-testid="plan-overlay-controls">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">Before / after on the customer’s photo</p>
        <div className="flex gap-2">
          {[['Show before', 100], ['Show after', 0], ['Half & half', 50]].map(([l, v]) => (
            <button key={l} type="button" onClick={() => setOverlay({ ...overlay, split: v })} className={btn} data-testid={`plan-ba-${v}`}>{l}</button>
          ))}
          <button type="button" onClick={onDownload} className={`${btn} border-slate-900 bg-slate-900 text-white`} data-testid="plan-ba-download"><Copy size={13} /> Download before &amp; after</button>
        </div>
      </div>
      <Slider label="Before ⟷ after" tid="plan-ba-split" min="0" max="100" step="1" value={overlay.split} onChange={set('split')} />
      <div className="grid sm:grid-cols-3 gap-x-6 gap-y-2">
        <Slider label="Photo zoom" tid="plan-photo-zoom" min="1" max="3" step="0.05" value={overlay.zoom} onChange={set('zoom')} />
        <Slider label="Move left/right" tid="plan-photo-dx" min="-50" max="50" step="1" value={overlay.dx} onChange={set('dx')} />
        <Slider label="Move up/down" tid="plan-photo-dy" min="-50" max="50" step="1" value={overlay.dy} onChange={set('dy')} />
      </div>
    </div>
  );
}

export function PlanToQuote({ totals, shapes, title, client, fmtA }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { user, refresh } = useAuth();
  const rows = totals.map((t) => {
    const unit = priceUnit(t.name);
    const mine = shapes.filter((s) => s.name === t.name);
    const qty = unit === 'item' ? mine.length : unit === 'm' ? mine.reduce((a, s) => a + wallSpan(s), 0) : t.a;
    return { name: t.name, unit, qty: +qty.toFixed(2), lines: t.lines };
  });
  const [prices, setPrices] = useState({});
  useEffect(() => {
    if (open) setPrices(Object.fromEntries(rows.map((r) => [r.name, priceFor(user, r.name)])));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = rows.reduce((a, r) => a + r.qty * (+prices[r.name] || 0), 0);

  const create = async () => {
    setBusy(true);
    const changed = Object.entries(prices).filter(([k, v]) => v !== '' && +v !== priceFor(user, k));
    if (changed.length) api.put('/me/plan-prices', { prices: { ...(user?.plan_prices || {}), ...Object.fromEntries(changed.map(([k, v]) => [k, +v])) } }).then(refresh).catch(() => {});
    const items = rows.map((r) => ({ description: `${r.name} — supply & install`, qty: r.qty, unit: r.unit, unit_price: +prices[r.name] || 0 }));
    const notes = `Materials (from plan): ${rows.map((r) => `${r.name}: ${r.lines.map((l) => `${l.name} ${l.text}`).join(', ')}`).join('; ')}`;
    try {
      const { data } = await api.post('/projects', { name: title || 'Garden / driveway plan', trade: 'groundworkers', customer_name: client || null, items, materials_cost: total, notes: notes.slice(0, 4000) });
      toast.success('Quote created from your plan'); nav(`/estimator/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create quote'); setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => (shapes.length ? setOpen(true) : toast.error('Add at least one area first'))} className="tm-btn tm-btn-dark inline-flex text-sm" data-testid="plan-to-quote-btn"><FileText size={15} /> Turn into quote</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="plan-quote-dialog">
          <DialogHeader><DialogTitle className="font-display">Turn this plan into a priced quote</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Prices come from your Settings → Price list; any changes here are saved back to it for every device. Material quantities go into the quote notes.</p>
          <div className="max-h-[50vh] overflow-y-auto divide-y divide-slate-100">
            {rows.map((r, i) => (
              <div key={r.name} className="flex items-center gap-3 py-2 text-sm" data-testid={`plan-quote-row-${i}`}>
                <span className="flex-1 min-w-0"><span className="font-semibold">{r.name}</span><span className="block text-xs text-slate-500">{r.unit === 'm²' ? fmtA(r.qty) : `${r.qty} ${unitLabel(r.unit, r.qty)}`}</span></span>
                <label className="flex items-center gap-1 text-xs text-slate-500">£
                  <input type="number" min="0" step="any" value={prices[r.name] ?? ''} onChange={(e) => setPrices({ ...prices, [r.name]: e.target.value })} className="tm-input w-24 min-h-0 py-1.5" data-testid={`plan-quote-price-${i}`} />
                  / {r.unit}
                </label>
                <span className="w-24 text-right font-semibold" data-testid={`plan-quote-line-${i}`}>£{(r.qty * (+prices[r.name] || 0)).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-slate-200 pt-3">
            <span className="text-sm">Subtotal (before labour, margin &amp; VAT): <span className="font-display text-xl font-semibold" data-testid="plan-quote-total">£{total.toFixed(2)}</span></span>
            <button type="button" onClick={create} disabled={busy} className="tm-btn" data-testid="plan-quote-create-btn">{busy ? 'Creating…' : 'Create quote'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
